import { createElement, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Menu, MenuProps } from '../Menu';
import { ContextMenuProps } from './';
import { useKeyboardShortcuts } from '../KeyboardShortcuts';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { BrowserName, getBrowserName, getOSName, OSName } from '../../util';
import { postfixTestId } from '../../util/testId';
import { usePopoverRoot } from '../../hooks/usePopoverRoot';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useMounted } from '../../hooks/useMounted';
import bundle from './NovaComponents-ContextMenu-gui-icu.properties';

import cx from 'classnames';
import cn from './ContextMenu.css.json';
import './ContextMenu.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const ContextMenu = (props: ContextMenuProps) => {
  // Destructure ContextMenu props before spreding onto Menu
  const {
    style,
    className,
    suppressIndicator,
    async,
    onContextMenu: onContextMenuFromProps,
    onItemSelect,
    children,
    ...menuProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<MenuProps['position']>({ x: 0, y: 0 });
  const focusRef = useRef<HTMLElement | null>(null);
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  const popoverRoot = usePopoverRoot();
  const mounted = useMounted();
  const { getActionsByKeyEvent } = useKeyboardShortcuts();

  const onContextMenu = (e?: React.MouseEvent) => {
    // ctrl + right, return browser defalut contextmenu.
    if (e?.ctrlKey && e.button === 2) return;

    onContextMenuFromProps?.(e);
    e?.preventDefault();

    if (props.sections || async) {
      e?.stopPropagation();
    }

    focusRef.current = document.activeElement as HTMLElement;
    if (!suppressIndicator && (props.sections || async)) {
      focusRef.current?.classList.add(cn.contextArea);
    }

    const pos = e
      ? {
          x: e.nativeEvent.clientX,
          y: e.nativeEvent.clientY,
        }
      : { dockingElement: focusRef.current };
    if (e?.button !== 2 && pos.x !== undefined && getBrowserName() === BrowserName.Firefox) {
      // NOVACOMMONS-3107: When open by keyboard in a dialog, FF is providing the "layout" position
      // rather than the "visual" position. Has to apply the transform of the dialog
      const dialog = focusRef.current.closest('[role=dialog]');
      if (dialog) {
        const translate = getComputedStyle(dialog).transform.match(/matrix\((.+)\)/);
        if (translate) {
          const [, , , , offsetX, offsetY] = translate[1].split(', ');
          pos.x += parseFloat(offsetX);
          pos.y += parseFloat(offsetY);
        }
      }
    }

    setPosition(pos);
    if (isOpen) {
      // NOVACOMMONS-4117
      setIsOpen(false);
      setTimeout(() => mounted.current && setIsOpen(true), 0);
    } else {
      setIsOpen(true);
    }
  };
  const close = useCallback((restoreFocus?: boolean) => {
    focusRef.current?.classList.remove(cn.contextArea);
    setIsOpen(false);
    restoreFocus && focusRef.current?.focus?.();
  }, []);

  const onRootContextMenu = useCallback(
    (e: MouseEvent) => {
      if (isOpen && (props.sections || async)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [isOpen, props.sections, async]
  );

  useEffect(() => {
    document.addEventListener('contextmenu', onRootContextMenu);
    return () => {
      document.removeEventListener('contextmenu', onRootContextMenu);
    };
  }, [onRootContextMenu]);

  return (
    <Fragment>
      <div
        className={cx(cn.contextContainer, className)}
        style={{
          ...renderInlineCssVars({
            'context-area-outline': `${dt.stateFocusNova.onRightClick.bd} ${dt.stateFocusNova.onRightClick.bdStyle} ${dt.stateFocusNova.onRightClick.bdWidth}`,
            'context-area-outline-offset': `-${dt.stateFocusNova.onRightClick.bdWidth}`,
          }),
          ...style,
        }}
        onContextMenu={onContextMenu}
        onKeyDown={(e) => {
          const { actions } = getActionsByKeyEvent(e.nativeEvent);
          if (
            actions.includes('openContextMenu') &&
            // Shift+F10 will generate contextmenu event on Windows so not go this route
            (getOSName() === OSName.Mac || !(e.shiftKey && e.key === 'F10'))
          ) {
            e.preventDefault();
            if (props.sections || async) {
              e.stopPropagation();
            }
            const target = e?.target as HTMLElement;
            const allMenuContent = popoverRoot?.querySelectorAll('[role="menu"]');

            if (allMenuContent && allMenuContent.length > 0) {
              for (const content of allMenuContent) {
                if (content && content.contains(target)) return;
              }
            }
            onContextMenu();
          }
        }}
      >
        {children}
        {props.sections || async ? (
          <Menu
            isOpen={isOpen}
            {...menuProps}
            sections={
              props.sections ?? [
                {
                  items: [
                    {
                      text: localize('loading.menuitem.label'),
                      testId: postfixTestId(props.testId, 'loading-menuitem'),
                    },
                  ],
                },
              ]
            }
            position={props.position ?? position}
            onItemSelected={(indices, selectedItemKey) => {
              if (!props.sections) return;
              onItemSelect?.(indices, selectedItemKey);
              close(true);
              props.onItemSelected?.(indices, selectedItemKey);
            }}
            onDismiss={(restoreFocus) => {
              close(restoreFocus);
              props.onDismiss?.();
            }}
          />
        ) : null}
      </div>
    </Fragment>
  );
};
